import { useEffect, useState, useMemo, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useStable } from "../context/StableContext";
import api, { errText, resolveImage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Printer, BookOpen, Search, Save, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const PX = 96 / 25.4; // px per mm on screen
const DEST_LABEL = { dpa: "DPA", ndpa: "NDPA", non_specificato: "Non specificato" };
const SECONDARY = ["dob", "sex", "breed", "microchip", "ueln", "destinazione", "owner"];

function fmtDate(v) {
  if (!v) return v;
  const m = String(v).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
}

const FIELD_DEFS = [
  { key: "logo", label: "Logo scuderia" },
  { key: "stable_name", label: "Nome scuderia" },
  { key: "dob", label: "Data di nascita" },
  { key: "sex", label: "Sesso" },
  { key: "breed", label: "Razza" },
  { key: "microchip", label: "Microchip" },
  { key: "ueln", label: "UELN" },
  { key: "destinazione", label: "DPA / NDPA" },
  { key: "owner", label: "Proprietario" },
];

const LABELS = Object.fromEntries(FIELD_DEFS.map((f) => [f.key, f.label]));

const STYLE_DEFAULT = {
  nameSize: 55, infoSize: 45, logoSize: 45,
  colorName: "#1B3B2B", colorInfo: "#1E2421", colorLabel: "#8C532B",
  borderStyle: "solid", borderWidth: 1.2, borderColor: "#1B3B2B",
  fieldOrder: [...SECONDARY], foldPos: "right", nameCase: "anagrafica",
};
const PRESETS = {
  essenziale: { fields: {}, ...STYLE_DEFAULT, nameSize: 66 },
  identificazione: { fields: { microchip: true, ueln: true, dob: true, breed: true }, ...STYLE_DEFAULT, nameSize: 52 },
  personalizzato: null,
};

// format/orient/fold -> full sheet + the printable cover box (in mm).
// For "fold", orient = orientation of the RESULTING A5 cover after folding.
function pageBox({ size, orient, fold }) {
  if (fold) {
    if (orient === "landscape") return { w: 210, h: 297, fold: "h", content: { w: 210, h: 148.5 } };
    return { w: 297, h: 210, fold: "v", content: { w: 148.5, h: 210 } };
  }
  const dims = size === "a5" ? [148, 210] : [210, 297];
  const [w, h] = orient === "landscape" ? [dims[1], dims[0]] : dims;
  return { w, h, fold: null, content: { w, h } };
}

// Name honours the desired slider size but shrinks automatically to never overflow.
function NameFit({ text, desiredMm, maxWmm, maxHmm, color }) {
  const ref = useRef(null);
  const [fs, setFs] = useState(desiredMm);
  useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    let size = desiredMm;
    el.style.fontSize = size + "mm";
    for (let i = 0; i < 80 && (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight); i++) {
      size -= 0.8; if (size <= 4) break; el.style.fontSize = size + "mm";
    }
    setFs(size);
  }, [text, desiredMm, maxWmm, maxHmm]);
  return (
    <div ref={ref} style={{
      fontSize: fs + "mm", width: maxWmm + "mm", maxHeight: maxHmm + "mm", color,
      fontWeight: 800, lineHeight: 1.32, paddingBottom: "0.3em", paddingTop: "0.08em", textAlign: "center", overflow: "hidden",
      display: "flex", alignItems: "center", justifyContent: "center", wordBreak: "break-word",
    }}>{text || "—"}</div>
  );
}

function CoverPage({ data, cfg, logoUrl, stableName }) {
  const box = pageBox(cfg);
  const cW = box.content.w, cH = box.content.h;
  const rows = (cfg.fieldOrder || SECONDARY).filter((k) => cfg.fields[k])
    .map((k) => {
      let v = data[k];
      if (k === "destinazione") v = DEST_LABEL[v] || null;
      else if (k === "dob") v = fmtDate(v);
      return v ? [LABELS[k], v] : null;
    }).filter(Boolean);
  const border = `${cfg.borderWidth}mm ${cfg.borderStyle} ${cfg.borderColor}`;
  const infoMm = 2.6 + (cfg.infoSize / 100) * 3.2;
  const logoMax = 10 + (cfg.logoSize / 100) * 30;
  const nameDesired = cW * (0.12 + (cfg.nameSize / 100) * 0.36);
  const content = (
    <div style={{ width: cW + "mm", height: cH + "mm", padding: "8mm", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, border, borderRadius: "3mm", padding: "7mm", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "4mm", boxSizing: "border-box", overflow: "hidden" }}>
        {cfg.fields.logo && logoUrl && <img src={logoUrl} alt="" style={{ maxHeight: logoMax + "mm", maxWidth: "72%", width: "auto", height: "auto", objectFit: "contain" }} />}
        {cfg.fields.stable_name && stableName && <div style={{ fontSize: (infoMm + 1) + "mm", color: cfg.colorLabel, fontWeight: 600, textAlign: "center" }}>{stableName}</div>}
        <NameFit text={cfg.nameCase === "upper" ? (data.name || "").toUpperCase() : data.name} desiredMm={nameDesired} maxWmm={cW - 24} maxHmm={cH * 0.44} color={cfg.colorName} />
        {rows.length > 0 && (
          <div style={{ marginTop: "2.5mm", display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "1.5mm 6mm", fontSize: infoMm + "mm" }}>
            {rows.map(([k, v]) => <div key={k}><span style={{ color: cfg.colorLabel }}>{k}: </span><span style={{ color: cfg.colorInfo }}>{v}</span></div>)}
          </div>
        )}
      </div>
    </div>
  );
  const sheet = { width: box.w + "mm", height: box.h + "mm", background: "#fff", display: "flex", boxSizing: "border-box", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,.12)" };
  if (box.fold === "v") {
    const pos = cfg.foldPos === "left" ? "left" : "right";
    const gutter = <div className="fold-line" style={{ width: cW + "mm", height: box.h + "mm", boxSizing: "border-box", [pos === "right" ? "borderRight" : "borderLeft"]: "0.3mm dashed #C9BCA0" }} />;
    return <div className="cover-page" style={sheet}>{pos === "right" ? <>{gutter}{content}</> : <>{content}{gutter}</>}</div>;
  }
  if (box.fold === "h") {
    const pos = cfg.foldPos === "top" ? "top" : "bottom";
    const gutter = <div className="fold-line" style={{ width: box.w + "mm", height: cH + "mm", boxSizing: "border-box", [pos === "bottom" ? "borderBottom" : "borderTop"]: "0.3mm dashed #C9BCA0" }} />;
    return <div className="cover-page" style={{ ...sheet, flexDirection: "column" }}>{pos === "bottom" ? <>{gutter}{content}</> : <>{content}{gutter}</>}</div>;
  }
  return <div className="cover-page" style={sheet}>{content}</div>;
}

// Scales a full sheet to fit the preview area (no scrollbars for a single page).
function PreviewPage({ scale, box, children }) {
  return (
    <div style={{ width: box.w * PX * scale, height: box.h * PX * scale, overflow: "hidden", flex: "0 0 auto", margin: "0 auto 14px" }}>
      <div style={{ transformOrigin: "top left", transform: `scale(${scale})`, width: box.w + "mm", height: box.h + "mm" }}>{children}</div>
    </div>
  );
}

function RangeRow({ label, minLabel, maxLabel, value, onChange, testId }) {
  return (
    <div>
      <p className="text-xs text-[#5F6B64] mb-0.5">{label}</p>
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-[#9AA39C] w-12 text-right">{minLabel}</span>
        <input type="range" min="0" max="100" value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testId} className="flex-1 accent-[#1B3B2B]" />
        <span className="text-[10px] text-[#9AA39C] w-12">{maxLabel}</span>
      </div>
    </div>
  );
}

function SwatchRow({ label, value, onChange, testId }) {
  const SW = ["#1B3B2B", "#8C532B", "#5F4626", "#111827", "#B91C1C", "#1D4ED8"];
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-[#5F6B64] w-24 shrink-0">{label}</span>
      <div className="flex items-center gap-1 flex-wrap">
        {SW.map((c) => (
          <button key={c} type="button" onClick={() => onChange(c)} aria-label={c}
            className={`h-6 w-6 rounded-full ${value === c ? "ring-2 ring-offset-1 ring-[#1B3B2B]" : "border border-[#E5DEC9]"}`} style={{ background: c }} />
        ))}
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="h-6 w-8 rounded border border-[#E5DEC9] p-0" />
      </div>
    </div>
  );
}

export default function Stampe() {
  const { current, perms } = useStable();
  const [sp] = useSearchParams();
  const [horses, setHorses] = useState([]);
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("anagrafica");
  const [selected, setSelected] = useState([]);
  const [free, setFree] = useState({ name: "", dob: "", sex: "", breed: "", microchip: "", ueln: "", owner: "", destinazione: "" });
  const [preset, setPreset] = useState("essenziale");
  const [cfg, setCfg] = useState({ fields: {}, size: "a4", orient: "portrait", fold: false, ...STYLE_DEFAULT });
  const canManage = perms?.canManage;
  const logoUrl = current?.logo ? resolveImage(current.logo) : null;

  const previewRef = useRef(null);
  const [pvSize, setPvSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = previewRef.current; if (!el) return;
    const upd = () => setPvSize({ w: el.clientWidth, h: el.clientHeight });
    const ro = new ResizeObserver(upd); ro.observe(el); upd();
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!current) return;
    api.get(`/stables/${current.id}/horses`).then((r) => setHorses((r.data || []).filter((h) => !h.archived)));
    api.get(`/stables/${current.id}/print-prefs`).then((r) => {
      const c = r.data?.cover;
      if (c) setCfg((prev) => ({ ...prev, ...c, fields: c.fields || {} }));
      if (c?.preset) setPreset(c.preset);
    }).catch(() => {});
    const hid = sp.get("horse");
    if (hid) setSelected([hid]);
  }, [current, sp]);

  const applyPreset = (p) => {
    setPreset(p);
    const def = PRESETS[p];
    if (def) setCfg((c) => ({ ...c, ...def, fields: { ...def.fields } }));
  };
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const toggleField = (k, v) => { setPreset("personalizzato"); setCfg((c) => ({ ...c, fields: { ...c.fields, [k]: v } })); };
  const moveField = (idx, dir) => setCfg((c) => {
    const o = [...(c.fieldOrder || SECONDARY)]; const j = idx + dir;
    if (j < 0 || j >= o.length) return c;
    [o[idx], o[j]] = [o[j], o[idx]];
    setPreset("personalizzato");
    return { ...c, fieldOrder: o };
  });

  const filtered = useMemo(() => horses.filter((h) => !q || (h.display_name || "").toLowerCase().includes(q.toLowerCase())), [horses, q]);
  const toggleHorse = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const covers = useMemo(() => {
    if (mode === "libera") return free.name.trim() ? [{ ...free, name: free.name.trim() }] : [];
    return selected.map((id) => {
      const h = horses.find((x) => x.id === id);
      return h ? { name: h.display_name, dob: h.dob, sex: h.sex, breed: h.breed, microchip: h.microchip, ueln: h.ueln, owner: h.owner_name, destinazione: h.destinazione } : null;
    }).filter(Boolean);
  }, [mode, free, selected, horses]);

  const box = pageBox(cfg);
  const scale = useMemo(() => {
    const availW = Math.max(80, pvSize.w - 24), availH = Math.max(120, (pvSize.h || 0) - 40);
    const s = Math.min(availW / (box.w * PX), availH / (box.h * PX));
    return Math.max(0.05, Math.min(s, 3));
  }, [pvSize, box.w, box.h]);

  const savePreset = async () => {
    try { await api.put(`/stables/${current.id}/print-prefs`, { cover: { ...cfg, preset } }); toast.success("Preset scuderia salvato"); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const doPrint = () => {
    if (covers.length === 0) { toast.error("Seleziona almeno un cavallo o inserisci un nome."); return; }
    window.print();
  };

  return (
    <div className="max-w-6xl mx-auto eq-fade-up">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[#1B3B2B]">Stampe</h1>
        <p className="text-sm text-[#5F6B64] mt-1">Centro stampe della scuderia. Le stampe restano gestite dai rispettivi moduli; qui puoi richiamarle rapidamente.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8 no-print">
        <div data-testid="print-hub-covers" className="bg-white rounded-xl border-2 border-[#1B3B2B] p-4 flex items-start gap-3">
          <BookOpen className="h-6 w-6 text-[#1B3B2B] shrink-0" />
          <div><p className="font-semibold text-[#1B3B2B]">Copertine libretti</p><p className="text-xs text-[#5F6B64]">Copertina cartacea per passaporti/libretti.</p></div>
        </div>
        <div className="bg-[#FBF9F4] rounded-xl border border-dashed border-[#E5DEC9] p-4 flex items-start gap-3 opacity-70">
          <Printer className="h-6 w-6 text-[#9AA39C] shrink-0" />
          <div><p className="font-semibold text-[#8C6D4F]">Altre stampe</p><p className="text-xs text-[#9AA39C]">In arrivo.</p></div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4 no-print" data-testid="cover-config">
          <h2 className="font-display text-xl font-bold text-[#1B3B2B]">Copertina libretto</h2>

          <div className="flex gap-2">
            <Button type="button" variant={mode === "anagrafica" ? "default" : "outline"} size="sm" data-testid="cover-mode-anagrafica"
              className={mode === "anagrafica" ? "bg-[#1B3B2B]" : ""} onClick={() => setMode("anagrafica")}>Da anagrafica</Button>
            <Button type="button" variant={mode === "libera" ? "default" : "outline"} size="sm" data-testid="cover-mode-libera"
              className={mode === "libera" ? "bg-[#1B3B2B]" : ""} onClick={() => setMode("libera")}>Copertina libera</Button>
          </div>

          {mode === "anagrafica" ? (
            <div>
              <div className="relative mb-2">
                <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9AA39C]" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cavallo…" className="pl-8 h-9" data-testid="cover-horse-search" />
              </div>
              <div className="max-h-52 overflow-auto rounded-lg border border-[#E5DEC9] bg-white divide-y divide-[#F0EAE1]">
                {filtered.map((h) => (
                  <label key={h.id} data-testid={`cover-horse-${h.id}`} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[#F7F3EC]">
                    <input type="checkbox" checked={selected.includes(h.id)} onChange={() => toggleHorse(h.id)} data-testid={`cover-horse-check-${h.id}`} />
                    <span className="text-[#1B3B2B]">{h.display_name}</span>
                  </label>
                ))}
                {filtered.length === 0 && <p className="px-3 py-3 text-xs text-[#9AA39C]">Nessun cavallo.</p>}
              </div>
              <p className="text-xs text-[#5F6B64] mt-1">{selected.length} selezionati</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div><Label>Nome cavallo *</Label><Input value={free.name} onChange={(e) => setFree((f) => ({ ...f, name: e.target.value }))} data-testid="cover-free-name" className="mt-1 h-9" placeholder="Es. Fulmine" /></div>
              <div className="grid grid-cols-2 gap-2">
                <Input value={free.microchip} onChange={(e) => setFree((f) => ({ ...f, microchip: e.target.value }))} placeholder="Microchip" className="h-9" data-testid="cover-free-microchip" />
                <Input value={free.breed} onChange={(e) => setFree((f) => ({ ...f, breed: e.target.value }))} placeholder="Razza" className="h-9" />
              </div>
              <p className="text-xs text-[#9AA39C]">Solo il nome è obbligatorio. Non crea un'anagrafica cavallo.</p>
            </div>
          )}

          <div>
            <Label>Preset</Label>
            <div className="flex gap-2 mt-1">
              {["essenziale", "identificazione", "personalizzato"].map((p) => (
                <Button key={p} type="button" size="sm" variant={preset === p ? "default" : "outline"} data-testid={`cover-preset-${p}`}
                  className={preset === p ? "bg-[#8C532B] capitalize" : "capitalize"} onClick={() => applyPreset(p)}>{p}</Button>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-[#E5DEC9] p-3 space-y-2">
            {["logo", "stable_name"].map((k) => (
              <label key={k} className="flex items-center justify-between gap-2 text-sm text-[#5F6B64]">
                <span>{LABELS[k]}</span>
                <Switch checked={!!cfg.fields[k]} onCheckedChange={(v) => toggleField(k, v)} data-testid={`cover-field-${k}`} />
              </label>
            ))}
            <div className="h-px bg-[#F0EAE1]" />
            <p className="text-[10px] text-[#9AA39C]">Informazioni secondarie — usa ↑/↓ per riordinarle.</p>
            {(cfg.fieldOrder || SECONDARY).map((k, idx, arr) => (
              <div key={k} className="flex items-center justify-between gap-2 text-sm text-[#5F6B64]" data-testid={`cover-fieldrow-${k}`}>
                <span className="flex items-center gap-1.5">
                  <span className="flex flex-col leading-none">
                    <button type="button" disabled={idx === 0} onClick={() => moveField(idx, -1)} data-testid={`cover-field-up-${k}`} className="text-[#8C6D4F] disabled:opacity-25"><ChevronUp className="h-3 w-3" /></button>
                    <button type="button" disabled={idx === arr.length - 1} onClick={() => moveField(idx, 1)} data-testid={`cover-field-down-${k}`} className="text-[#8C6D4F] disabled:opacity-25"><ChevronDown className="h-3 w-3" /></button>
                  </span>
                  {LABELS[k]}
                </span>
                <Switch checked={!!cfg.fields[k]} onCheckedChange={(v) => toggleField(k, v)} data-testid={`cover-field-${k}`} />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Formato</Label>
              <Select value={cfg.fold ? "fold" : cfg.size} onValueChange={(v) => v === "fold" ? setCfg((c) => ({ ...c, fold: true, size: "a4", foldPos: c.orient === "landscape" ? "bottom" : "right" })) : setCfg((c) => ({ ...c, fold: false, size: v }))}>
                <SelectTrigger className="mt-1 h-9" data-testid="cover-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="a4">A4</SelectItem>
                  <SelectItem value="a5">A5</SelectItem>
                  <SelectItem value="fold">A4 piegato → A5</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Orientamento{cfg.fold ? " copertina" : ""}</Label>
              <Select value={cfg.orient} onValueChange={(v) => setCfg((c) => ({ ...c, orient: v, foldPos: c.fold ? (v === "landscape" ? "bottom" : "right") : c.foldPos }))}>
                <SelectTrigger className="mt-1 h-9" data-testid="cover-orient"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="portrait">Verticale</SelectItem>
                  <SelectItem value="landscape">Orizzontale</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {cfg.fold && <p className="text-[11px] text-[#9AA39C] -mt-2">L'orientamento si riferisce alla copertina A5 risultante dopo la piega.</p>}
          {cfg.fold && (
            <div><Label>Posizione copertina</Label>
              <Select value={cfg.foldPos} onValueChange={(v) => set("foldPos", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="cover-fold-pos"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cfg.orient === "landscape"
                    ? [<SelectItem key="top" value="top">Sopra</SelectItem>, <SelectItem key="bottom" value="bottom">Sotto</SelectItem>]
                    : [<SelectItem key="left" value="left">Sinistra</SelectItem>, <SelectItem key="right" value="right">Destra</SelectItem>]}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-3 rounded-lg border border-[#E5DEC9] p-3">
            <div>
              <p className="text-xs text-[#5F6B64] mb-1">Nome cavallo</p>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={cfg.nameCase !== "upper" ? "default" : "outline"} data-testid="cover-namecase-anagrafica"
                  className={cfg.nameCase !== "upper" ? "bg-[#1B3B2B]" : ""} onClick={() => set("nameCase", "anagrafica")}>Come anagrafica</Button>
                <Button type="button" size="sm" variant={cfg.nameCase === "upper" ? "default" : "outline"} data-testid="cover-namecase-upper"
                  className={cfg.nameCase === "upper" ? "bg-[#1B3B2B]" : ""} onClick={() => set("nameCase", "upper")}>MAIUSCOLO</Button>
              </div>
            </div>
            <RangeRow label="Dimensione nome" minLabel="Piccolo" maxLabel="Grande" value={cfg.nameSize} onChange={(v) => set("nameSize", v)} testId="cover-name-size" />
            <RangeRow label="Dimensione informazioni" minLabel="Piccole" maxLabel="Grandi" value={cfg.infoSize} onChange={(v) => set("infoSize", v)} testId="cover-info-size" />
            {cfg.fields.logo && <RangeRow label="Dimensione logo" minLabel="Piccolo" maxLabel="Grande" value={cfg.logoSize} onChange={(v) => set("logoSize", v)} testId="cover-logo-size" />}
          </div>

          <div className="space-y-2 rounded-lg border border-[#E5DEC9] p-3">
            <SwatchRow label="Testo principale" value={cfg.colorName} onChange={(v) => set("colorName", v)} testId="cover-color-name" />
            <SwatchRow label="Testi secondari" value={cfg.colorInfo} onChange={(v) => set("colorInfo", v)} testId="cover-color-info" />
            <SwatchRow label="Titoli / etichette" value={cfg.colorLabel} onChange={(v) => set("colorLabel", v)} testId="cover-color-label" />
            <SwatchRow label="Bordo" value={cfg.borderColor} onChange={(v) => set("borderColor", v)} testId="cover-border-color" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Bordo</Label>
              <Select value={cfg.borderStyle} onValueChange={(v) => set("borderStyle", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="cover-border-style"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="solid">Continuo</SelectItem>
                  <SelectItem value="double">Doppio</SelectItem>
                  <SelectItem value="dashed">Tratteggiato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Spessore</Label>
              <Select value={String(cfg.borderWidth)} onValueChange={(v) => set("borderWidth", Number(v))}>
                <SelectTrigger className="mt-1 h-9" data-testid="cover-border-width"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.6">Sottile</SelectItem>
                  <SelectItem value="1.2">Medio</SelectItem>
                  <SelectItem value="2.5">Spesso</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button onClick={doPrint} data-testid="cover-print-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]"><Printer className="h-4 w-4 mr-2" />Stampa ({covers.length})</Button>
            {canManage && <Button variant="outline" onClick={savePreset} data-testid="cover-save-preset"><Save className="h-4 w-4 mr-2" />Salva preset scuderia</Button>}
          </div>
        </div>

        <div>
          <p className="text-xs text-[#8C532B] font-mono-label mb-2 no-print">Anteprima</p>
          <div ref={previewRef} className="rounded-lg bg-[#EDE7DA] p-3 no-print flex flex-col items-center" style={{ height: "64vh", overflowX: "hidden", overflowY: covers.length > 1 ? "auto" : "hidden" }} data-testid="cover-preview">
            {covers.length === 0 ? <p className="text-sm text-[#9AA39C] p-6 text-center m-auto">Seleziona un cavallo o inserisci un nome per vedere l'anteprima.</p> : (
              covers.map((d, i) => <PreviewPage key={i} scale={scale} box={box}><CoverPage data={d} cfg={cfg} logoUrl={logoUrl} stableName={current?.name} /></PreviewPage>)
            )}
          </div>
          {createPortal(
            <div className="eq-print-portal" aria-hidden>
              <style>{`@page { size: ${box.w}mm ${box.h}mm; margin: 0; }`}</style>
              {covers.map((d, i) => <CoverPage key={i} data={d} cfg={cfg} logoUrl={logoUrl} stableName={current?.name} />)}
            </div>,
            document.body
          )}
        </div>
      </div>
    </div>
  );
}
